import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

function SectionSeparator({ title }) {
  return (
    <div className="w-full flex justify-center items-center py-0 mb-10 overflow-hidden">
      <div className="relative flex items-center justify-center w-full max-w-7xl px-4 md:px-8 gap-6 md:gap-10">
        <motion.div
          initial={{ opacity: 0, width: "0%" }}
          whileInView={{ opacity: 1, width: "100%" }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="h-[1px] bg-gradient-to-r from-transparent via-[#D4AF37] to-[#D4AF37] w-full flex-1 opacity-50"
        />

        {title ? (
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="flex-shrink-0 font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-[#14213D] font-normal text-center leading-tight max-w-[80%] md:max-w-none px-2"
          >
            {title}
          </motion.h2>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0, rotate: -45 }}
            whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: "backOut" }}
            className="text-[#D4AF37] flex-shrink-0"
          >
            <div className="w-3 h-3 border border-[#D4AF37] rotate-45 bg-[#D4AF37]/20" />
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, width: "0%" }}
          whileInView={{ opacity: 1, width: "100%" }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="h-[1px] bg-gradient-to-l from-transparent via-[#D4AF37] to-[#D4AF37] w-full flex-1 opacity-50"
        />
      </div>
    </div>
  );
}

export default function Explore({ images = [], excludeCategory }) {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [cardsPerView, setCardsPerView] = useState(4);
  const [loadedImages, setLoadedImages] = useState([]);
  const [redirectedItems, setRedirectedItems] = useState(new Set());

  const isPaused = useRef(false);
  const dragStartX = useRef(0);
  const dragStartY = useRef(0);
  const dragStartPos = useRef(0);
  const isDragging = useRef(false);
  const dragMoved = useRef(false);

  useEffect(() => {
    const preload = (images || []).map(
      (src) =>
        new Promise((resolve) => {
          const img = new Image();
          img.src = src;
          img.onload = () => resolve(src);
          img.onerror = () => resolve(images?.[0] || "");
        })
    );
    Promise.all(preload).then(setLoadedImages);
  }, [images]);

  useEffect(() => {
    const updateCards = () => {
      const w = window.innerWidth;
      if (w >= 2560) setCardsPerView(4);
      else if (w >= 1920) setCardsPerView(5);
      else if (w >= 1536) setCardsPerView(5);
      else if (w >= 1280) setCardsPerView(4);
      else if (w >= 1024) setCardsPerView(3);
      else if (w >= 768) setCardsPerView(2);
      else setCardsPerView(1.3);
    };
    updateCards();
    window.addEventListener("resize", updateCards);
    return () => window.removeEventListener("resize", updateCards);
  }, []);

  const collections = useMemo(
    () => {
      const allCollections = [
        {
          id: "suit",
          name: "Suit / Jodhpuri",
          image: loadedImages[0] || images?.[0] || "",
          headerImage: "https://cdn.pixabay.com/photo/2021/12/15/13/45/men-6872634_1280.jpg",
          ref: "suit/select_cloth/personalize",
          active: true,
          title: "Design Formal and Jodhpuri / Bandhgala Suits That Define Your Style",
          subtitle: "Choose your fabric, tailor your fit, and bring your vision to life.",
        },
        {
          id: "kurta",
          name: "Kurta Set",
          image: loadedImages[3] || images?.[3] || "",
          headerImage: "https://cdn.pixabay.com/photo/2021/12/15/13/45/men-6872634_1280.jpg",
          ref: "kurta/select_cloth/personalize",
          active: true,
          title: "Design Kurta / Pathani Suits That Fit You Perfectly",
          subtitle: "Select your favorite fabrics and styles for traditional elegance.",
        },
        {
          id: "blazer",
          name: "Blazer Set",
          image: loadedImages[2] || images?.[2] || "",
          headerImage: "https://cdn.pixabay.com/photo/2021/12/15/13/45/men-6872634_1280.jpg",
          ref: "blazer/select_cloth/personalize",
          active: true,
          title: "Design Stylish Blazers That Elevate Your Look",
          subtitle: "Pick your fabric, cut, and style for a sharp, modern outfit.",
        },
        {
          id: "pant",
          name: "Trouser",
          image: loadedImages[6] || images?.[6] || "",
          headerImage: "https://cdn.pixabay.com/photo/2021/12/15/13/45/men-6872634_1280.jpg",
          ref: "pant/select_cloth/personalize",
          active: true,
          title: "Custom Tailored Trousers for the Perfect Fit",
          subtitle: "Choose fabrics, styles, and cuts that complement your wardrobe.",
        },
        {
          id: "shirt",
          name: "Shirt",
          image: loadedImages[5] || images?.[5] || "",
          headerImage:
            "https://media.istockphoto.com/id/1293366109/photo/this-one-match-perfect-with-me.jpg?s=1024x1024&w=is&k=20&c=ljpyNgsm90gjZHYRuHWfLXU-PTZk7IERjJW5v3NZ70U=",
          ref: "shirt/select_cloth/personalize",
          active: true,
          title: "Design Stylish Shirts That Stand Out",
          subtitle: "Customize your shirt to get the perfect fit and fabric.",
        },
        {
          id: "sadri",
          name: "Jacket / Sadri",
          image: loadedImages[10] || images?.[10] || "",
          headerImage:
            "https://media.istockphoto.com/id/1293366109/photo/this-one-match-perfect-with-me.jpg?s=1024x1024&w=is&k=20&c=ljpyNgsm90gjZHYRuHWfLXU-PTZk7IERjJW5v3NZ70U=",
          ref: "sadri/select_cloth/personalize",
          active: true,
          title: "Tailored Jackets & Sadri Suits for Every Occasion",
          subtitle: "Select from premium fabrics and styles for a refined look.",
        },
        {
          id: "uniform",
          name: "Uniform",
          image: loadedImages[4] || images?.[4] || "",
          headerImage: "https://media.istockphoto.com/id/1058372914/photo/indian-stylish-man-in-yellow-traditional-clothes.jpg",
          ref: null,
          active: false,
        },
        {
          id: "sherwani",
          name: "Sharwani",
          image: loadedImages[7] || images?.[7] || "",
          headerImage: "https://media.istockphoto.com/id/1058372914/photo/indian-stylish-man-in-yellow-traditional-clothes.jpg",
          ref: null,
          active: false,
        },
        {
          id: "jubba",
          name: "Jubba",
          image: loadedImages[8] || images?.[8] || "",
          headerImage: "https://media.istockphoto.com/id/1058372914/photo/indian-stylish-man-in-yellow-traditional-clothes.jpg",
          ref: null,
          active: false,
        },
      ];

      if (excludeCategory) {
        return allCollections.filter(
          (col) =>
            col.id !== excludeCategory &&
            !col.name.toLowerCase().includes(excludeCategory.toLowerCase())
        );
      }
      return allCollections;
    },
    [loadedImages, images, excludeCategory]
  );

  useEffect(() => {
    if (!containerRef.current || loadedImages.length === 0) return;
    const container = containerRef.current;
    let pos = 0;
    const speed = 1;
    let directionLocked = null;

    const animate = () => {
      if (!isPaused.current && !isDragging.current) {
        pos += speed;
        if (pos >= container.scrollWidth / 2) pos -= container.scrollWidth / 2;
        container.style.transform = `translateX(-${pos}px)`;
      }
      requestAnimationFrame(animate);
    };
    const requestId = requestAnimationFrame(animate);

    const onPointerDown = (e) => {
      isDragging.current = true;
      isPaused.current = true;
      dragMoved.current = false;
      dragStartX.current = e.type === "touchstart" ? e.touches[0].clientX : e.clientX;
      dragStartY.current = e.type === "touchstart" ? e.touches[0].clientY : e.clientY;
      dragStartPos.current = pos;
      directionLocked = null;
      container.style.cursor = "grabbing";
    };

    const onPointerMove = (e) => {
      if (!isDragging.current) return;
      const x = e.type === "touchmove" ? e.touches[0].clientX : e.clientX;
      const y = e.type === "touchmove" ? e.touches[0].clientY : e.clientY;
      const deltaX = x - dragStartX.current;
      const deltaY = y - dragStartY.current;

      if (directionLocked === null && Math.abs(deltaX) + Math.abs(deltaY) > 10) {
        directionLocked = Math.abs(deltaX) > Math.abs(deltaY) ? "horizontal" : "vertical";
      }

      if (directionLocked === "horizontal") {
        e.preventDefault();
        dragMoved.current = true;
        pos = dragStartPos.current - deltaX;
        if (pos >= container.scrollWidth / 2) pos -= container.scrollWidth / 2;
        if (pos < 0) pos += container.scrollWidth / 2;
        container.style.transform = `translateX(-${pos}px)`;
      }
    };

    const onPointerUp = () => {
      isDragging.current = false;
      isPaused.current = false;
      directionLocked = null;
      container.style.cursor = "grab";
    };

    container.addEventListener("mousedown", onPointerDown);
    container.addEventListener("mousemove", onPointerMove);
    container.addEventListener("mouseup", onPointerUp);
    container.addEventListener("mouseleave", onPointerUp);
    container.addEventListener("touchstart", onPointerDown, { passive: false });
    container.addEventListener("touchmove", onPointerMove, { passive: false });
    container.addEventListener("touchend", onPointerUp);

    return () => {
      cancelAnimationFrame(requestId);
      container.removeEventListener("mousedown", onPointerDown);
      container.removeEventListener("mousemove", onPointerMove);
      container.removeEventListener("mouseup", onPointerUp);
      container.removeEventListener("mouseleave", onPointerUp);
      container.removeEventListener("touchstart", onPointerDown);
      container.removeEventListener("touchmove", onPointerMove);
      container.removeEventListener("touchend", onPointerUp);
    };
  }, [loadedImages]);

  const handleClickNavigate = (idx, col) => {
    if (dragMoved.current) return;
    if (!col.active) return;
    if (!redirectedItems.has(idx) && col.ref) {
      navigate(`/custom/${col.ref}`, {
        state: {
          headerTitle: col.title,
          headerSubtitle: col.subtitle,
          headerImage: col.headerImage,
        },
      });
      setRedirectedItems((prev) => new Set(prev).add(idx));
    }
  };

  return (
    <div id="explore" className="bg-white relative z-10 py-[50px]">
      <div className="w-full px-[10px] text-center">
        <SectionSeparator title="Custom Tailored By Categories" />

        <div className="relative w-full mt-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-24 h-full z-20 pointer-events-none bg-gradient-to-r from-white via-white/80 to-transparent hidden md:block" />
          <div className="absolute top-0 right-0 w-24 h-full z-20 pointer-events-none bg-gradient-to-l from-white via-white/80 to-transparent hidden md:block" />

          <div
            ref={containerRef}
            className="flex flex-nowrap cursor-grab select-none"
            style={{ willChange: "transform", userSelect: "none", touchAction: "pan-y" }}
            onMouseEnter={() => (isPaused.current = true)}
            onMouseLeave={() => (isPaused.current = false)}
            onTouchStart={() => (isPaused.current = true)}
            onTouchEnd={() => (isPaused.current = false)}
          >
            {[...collections, ...collections].map((col, idx) => (
              <div
                key={`${col.name}-${idx}`}
                style={{ flex: `0 0 ${100 / cardsPerView}%` }}
                className="mx-2"
                onClick={() => handleClickNavigate(idx, col)}
              >
                <div className="bg-white rounded-2xl shadow-md hover:shadow-xl overflow-hidden flex flex-col h-full">
                  <div className="w-full 2xl:aspect-[4/5] flex justify-center items-center overflow-hidden rounded-t-2xl bg-gray-100">
                    <img
                      src={col.image}
                      alt={col.name}
                      draggable="false"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 pointer-events-none select-none"
                    />
                  </div>
                  <div className="bg-[#EDE0CC] px-[10px] py-3 flex flex-col flex-grow rounded-b-2xl">
                    <h3 className="font-heading font-semibold text-[clamp(1.2rem,1.5vw,2rem)] text-black truncate text-left">{col.name}</h3>
                    <div className="flex items-center justify-between mt-2">
                      {col.active ? (
                        <>
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClickNavigate(idx, col);
                            }}
                            className="font-body font-medium text-[clamp(1rem,1.2vw,1.5rem)] text-[#1D1D1D] hover:text-[#D4AF37] transition-colors"
                          >
                            Design Now
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              col.active && col.ref &&
                                navigate(`/custom/${col.ref}`, {
                                  state: {
                                    headerTitle: col.title,
                                    headerSubtitle: col.subtitle,
                                    headerImage: col.headerImage,
                                  },
                                });
                            }}
                            className="w-[clamp(2.5rem,3vw,4rem)] h-[clamp(2.5rem,3vw,4rem)] rounded-full flex items-center justify-center bg-black text-white transition-all duration-500 ease-in-out hover:bg-[#F4D58D] hover:text-black hover:rotate-45 border-0 !border-none outline-none ring-0 shadow-none"
                            style={{ border: "none !important", outline: "none !important" }}
                          >
                            <ArrowUpRight className="w-[clamp(1.25rem,1.5vw,2rem)] h-[clamp(1.25rem,1.5vw,2rem)]" />
                          </button>
                        </>
                      ) : (
                        <span className="text-sm text-gray-500 italic">Coming Soon</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
